import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Package, Mail, MapPin, Armchair } from 'lucide-react';

// Parse event name: "M13 - Day 13 - Men's Semifinal #1 - Day Session - Chatrier"
// Returns { match, session, court }
function parseEvent(evenement) {
  if (!evenement) return { match: 'Inconnu', session: '', court: '' };
  const parts = evenement.split(' - ').map(p => p.trim());
  // First part is match (M13), last part is court, "Day/Evening Session" is session
  const match = parts.slice(0, 3).join(' - '); // M13 - Day 13 - Men's Semifinal #1
  let session = '';
  let court = '';
  for (const p of parts) {
    if (/session/i.test(p)) session = p;
    if (/chatrier|lenglen|suzanne|simonne|annexe|court/i.test(p)) court = p;
  }
  return { match, session, court, full: evenement };
}

function Badge({ children, variant = 'default', className = '' }) {
  const base = 'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium';
  const variants = {
    default: 'bg-zinc-700 text-zinc-300',
    green: 'bg-green-500/20 text-green-400',
    orange: 'bg-orange-500/20 text-orange-400',
    red: 'bg-red-500/20 text-red-400',
    blue: 'bg-blue-500/20 text-blue-400',
  };
  return <span className={`${base} ${variants[variant] || variants.default} ${className}`}>{children}</span>;
}

function ResteBadge({ reste }) {
  if (reste === 0) return <Badge variant="green">COMPLET</Badge>;
  return <Badge variant="orange">RESTE {reste}</Badge>;
}

function PlaceDetail({ ticket }) {
  const place = ticket.place || '';
  const placeFin = ticket.place_fin || '';
  const placeStr = placeFin && placeFin !== place ? `${place}-${placeFin}` : place || '-';
  const isAttrib = ticket.attribue === true || (ticket.ref_vente && ticket.ref_vente.trim());
  const refVente = ticket.ref_vente || '';

  return (
    <div className={`flex items-center gap-3 px-3 py-1.5 text-xs rounded ${isAttrib ? 'bg-green-500/5 border-l-2 border-green-500/40' : 'bg-zinc-800/50 border-l-2 border-zinc-600/40'}`}>
      <span className="text-zinc-500 w-28 truncate" title={ticket.ref_achat}>REF: {ticket.ref_achat || '-'}</span>
      {ticket.escalier && <span className="text-zinc-500">Esc.{ticket.escalier}</span>}
      {ticket.rang && <span className="text-zinc-500">Rg.{ticket.rang}</span>}
      <span className="text-zinc-300 font-mono">Pl. {placeStr}</span>
      {isAttrib ? (
        <span className="text-green-400 ml-auto">attribué{refVente ? ` → ${refVente}` : ''}</span>
      ) : (
        <span className="text-orange-400 ml-auto">disponible</span>
      )}
    </div>
  );
}

export default function RecapAchat({ tickets, project }) {
  const [expandedSources, setExpandedSources] = useState({});
  const [expandedComptes, setExpandedComptes] = useState({});
  const [expandedEvents, setExpandedEvents] = useState({});
  const [expandedCats, setExpandedCats] = useState({});

  // Filter ACHAT tickets only
  const achats = useMemo(() => tickets.filter(t => t.type === 'ACHAT'), [tickets]);

  // Build hierarchy: Source > Compte > Event > Category > tickets
  const tree = useMemo(() => {
    const sourceMap = {};

    for (const t of achats) {
      const src = t.source || 'SANS SOURCE';
      const compte = t.email_compte || 'sans email';
      const event = t.evenement || 'Inconnu';
      const cat = t.categorie || 'SANS CAT';

      if (!sourceMap[src]) sourceMap[src] = { comptes: {}, total: 0, attrib: 0 };
      sourceMap[src].total++;
      if (t.attribue || (t.ref_vente && t.ref_vente.trim())) sourceMap[src].attrib++;

      const srcObj = sourceMap[src];
      if (!srcObj.comptes[compte]) srcObj.comptes[compte] = { events: {}, total: 0, attrib: 0 };
      srcObj.comptes[compte].total++;
      if (t.attribue || (t.ref_vente && t.ref_vente.trim())) srcObj.comptes[compte].attrib++;

      const compteObj = srcObj.comptes[compte];
      if (!compteObj.events[event]) compteObj.events[event] = { cats: {}, total: 0, attrib: 0, parsed: parseEvent(event) };
      compteObj.events[event].total++;
      if (t.attribue || (t.ref_vente && t.ref_vente.trim())) compteObj.events[event].attrib++;

      const eventObj = compteObj.events[event];
      if (!eventObj.cats[cat]) eventObj.cats[cat] = { tickets: [], total: 0, attrib: 0 };
      eventObj.cats[cat].tickets.push(t);
      eventObj.cats[cat].total++;
      if (t.attribue || (t.ref_vente && t.ref_vente.trim())) eventObj.cats[cat].attrib++;
    }

    // Sort sources by total desc
    return Object.entries(sourceMap)
      .sort(([, a], [, b]) => b.total - a.total)
      .map(([name, data]) => ({ name, ...data }));
  }, [achats]);

  const toggle = (setter, key) => setter(prev => ({ ...prev, [key]: !prev[key] }));
  const Arrow = ({ open }) => open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />;

  // Only for RG
  if (project !== 'ROLAND GARROS') return null;

  if (achats.length === 0) {
    return <div className="text-zinc-500 text-sm py-4 text-center">Aucun billet ACHAT trouvé</div>;
  }

  // Global stats
  const totalAll = achats.length;
  const attribAll = achats.filter(t => t.attribue || (t.ref_vente && t.ref_vente.trim())).length;

  return (
    <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1" data-testid="recap-achat-panel">
      {/* Global summary */}
      <div className="flex items-center gap-3 px-3 py-2 bg-zinc-800/60 rounded-lg border border-zinc-700/50 mb-2">
        <Package className="w-4 h-4 text-amber-400" />
        <span className="text-sm text-zinc-300 font-medium">
          {totalAll} ACHAT{totalAll > 1 ? 'S' : ''}
        </span>
        <Badge variant="green">{attribAll} attribués</Badge>
        <ResteBadge reste={totalAll - attribAll} />
        <span className="text-zinc-600 text-xs ml-auto">{tree.length} source{tree.length > 1 ? 's' : ''}</span>
      </div>

      {/* Source level */}
      {tree.map(src => {
        const srcKey = src.name;
        const srcOpen = expandedSources[srcKey];
        const compteEntries = Object.entries(src.comptes).sort(([, a], [, b]) => b.total - a.total);

        return (
          <div key={srcKey} className="border border-zinc-700/40 rounded-lg overflow-hidden">
            {/* Source header */}
            <button
              onClick={() => toggle(setExpandedSources, srcKey)}
              className="w-full flex items-center gap-2 px-3 py-2 bg-zinc-800/80 hover:bg-zinc-700/60 transition-colors text-left"
              data-testid={`recap-source-${srcKey}`}
            >
              <Arrow open={srcOpen} />
              <Package className="w-4 h-4 text-amber-400" />
              <span className="font-semibold text-sm text-zinc-200">{srcKey}</span>
              <Badge>{compteEntries.length} compte{compteEntries.length > 1 ? 's' : ''}</Badge>
              <span className="text-xs text-zinc-400 ml-auto mr-2">{src.total} billets</span>
              <Badge variant="green">{src.attrib} attrib.</Badge>
              <ResteBadge reste={src.total - src.attrib} />
            </button>

            {/* Compte level */}
            {srcOpen && (
              <div className="border-t border-zinc-700/30">
                {compteEntries.map(([compteName, compteData]) => {
                  const compteKey = `${srcKey}::${compteName}`;
                  const compteOpen = expandedComptes[compteKey];
                  const eventEntries = Object.entries(compteData.events).sort(([a], [b]) => a.localeCompare(b));

                  return (
                    <div key={compteKey} className="border-b border-zinc-700/20 last:border-b-0">
                      <button
                        onClick={() => toggle(setExpandedComptes, compteKey)}
                        className="w-full flex items-center gap-2 px-5 py-1.5 hover:bg-zinc-800/50 transition-colors text-left"
                      >
                        <Arrow open={compteOpen} />
                        <Mail className="w-3.5 h-3.5 text-blue-400" />
                        <span className="text-xs text-zinc-300 truncate max-w-[200px]" title={compteName}>{compteName}</span>
                        <span className="text-xs text-zinc-500 ml-auto mr-2">{compteData.total} bil.</span>
                        <Badge variant="green">{compteData.attrib}</Badge>
                        <ResteBadge reste={compteData.total - compteData.attrib} />
                      </button>

                      {/* Event level */}
                      {compteOpen && (
                        <div className="ml-6 border-l border-zinc-700/30">
                          {eventEntries.map(([eventName, eventData]) => {
                            const eventKey = `${compteKey}::${eventName}`;
                            const eventOpen = expandedEvents[eventKey];
                            const { parsed } = eventData;
                            const catEntries = Object.entries(eventData.cats).sort(([a], [b]) => a.localeCompare(b));

                            return (
                              <div key={eventKey}>
                                <button
                                  onClick={() => toggle(setExpandedEvents, eventKey)}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-800/40 transition-colors text-left"
                                >
                                  <Arrow open={eventOpen} />
                                  <MapPin className="w-3.5 h-3.5 text-purple-400" />
                                  <span className="text-xs text-zinc-300 truncate max-w-[300px]" title={eventName}>
                                    {parsed.match}
                                  </span>
                                  {parsed.session && <Badge variant="blue">{parsed.session}</Badge>}
                                  {parsed.court && <span className="text-xs text-zinc-500">{parsed.court}</span>}
                                  <span className="text-xs text-zinc-500 ml-auto mr-2">{eventData.total}</span>
                                  <span className="text-xs text-green-400">{eventData.attrib}</span>
                                  <span className="text-zinc-600 text-xs">/</span>
                                  <span className={`text-xs ${eventData.total - eventData.attrib > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                                    R:{eventData.total - eventData.attrib}
                                  </span>
                                </button>

                                {/* Category level with ticket details */}
                                {eventOpen && (
                                  <div className="ml-6 border-l border-zinc-700/20">
                                    {catEntries.map(([catName, catData]) => {
                                      const catKey = `${eventKey}::${catName}`;
                                      const catOpen = expandedCats[catKey];

                                      return (
                                        <div key={catKey}>
                                          <button
                                            onClick={() => toggle(setExpandedCats, catKey)}
                                            className="w-full flex items-center gap-2 px-3 py-1 hover:bg-zinc-800/30 transition-colors text-left"
                                          >
                                            <Arrow open={catOpen} />
                                            <Armchair className="w-3 h-3 text-zinc-500" />
                                            <span className="text-xs text-zinc-400">{catName}</span>
                                            <span className="text-xs text-zinc-500 ml-auto mr-2">{catData.total}</span>
                                            <span className="text-xs text-green-400">{catData.attrib}</span>
                                            <span className="text-zinc-600 text-xs">/</span>
                                            <span className={`text-xs ${catData.total - catData.attrib > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                                              R:{catData.total - catData.attrib}
                                            </span>
                                          </button>

                                          {/* Ticket details */}
                                          {catOpen && (
                                            <div className="ml-6 space-y-0.5 py-1">
                                              {catData.tickets
                                                .sort((a, b) => {
                                                  const pa = parseInt(a.place) || 0;
                                                  const pb = parseInt(b.place) || 0;
                                                  return pa - pb;
                                                })
                                                .map(t => <PlaceDetail key={t.id} ticket={t} />)}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
